import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'
import type { AgentFocus } from '@tunasong/agent-context'

type tools = [
  'Settings',
  'Chat',
  'Activities',
  'Agents',
  'My Notes',
  'Pool',
  'Context',
  'Suggestions',
  'Record',
  'Guitar',
  'Debug',
]
export type SidebarToolName = tools[number]

// Define a type for the slice state
export interface UIState {
  sidebar: {
    open: boolean
    activeTool: SidebarToolName | null
    /**
     * Id of the agent whose chat thread is currently surfaced in the Agents
     * sidebar tool. Mirrored by the `AgentRail` highlight + the in-panel Tabs
     * so both surfaces stay in sync. Cleared when `activeTool` changes away
     * from `'Agents'` so the next time the panel opens it starts fresh.
     */
    activeAgentId: string | null
    /**
     * One-shot anchor handed off to the Agents sidebar composer when the user
     * clicks an agent-contributed block affordance. `AgentChat` reads this on
     * mount/update, renders a dismissible focus chip, and includes it on the
     * outgoing `agentStream` dispatch — then clears via this same action.
     */
    pendingFocus: AgentFocus | null
    /**
     * One-shot prefilled composer text handed off alongside `pendingFocus`
     * (e.g., `'Ask chord advisor'` template message). Consumed by the chat
     * box, then cleared.
     */
    pendingDraft: string | null
    /**
     * When true, the `pendingDraft` is sent immediately as an agent turn rather
     * than pre-filled into the composer (one-click contextual commands). The
     * Agents sidebar routes the draft to `Chat.autoSubmit` instead of the
     * ChatBox, then clears this flag. False for plain pre-fill (empty-state
     * prompt chips).
     */
    pendingAutoSend: boolean
  }
  /**
   * Cross-component coordination for inline-anchored personal notes. When a user
   * clicks an anchor marker (or just-created their note), the marker handler sets
   * this id; the My Notes panel reads it on mount, expands the matching row, and
   * clears it via `setPendingAnchoredNoteId(null)`.
   */
  pendingAnchoredNoteId: string | null
  /**
   * Element id of the block whose personal note is currently expanded in the
   * sidebar. Drives the editor-side `data-anchored-active` highlight and the
   * 600ms spotlight pulse. `null` when no note is expanded (or the expanded
   * note has no `targetElementId`).
   */
  activeAnchoredElementId: string | null
  /**
   * Element id previewed by the sidebar's hover or focus on a note row. Drives
   * a lighter `data-anchored-hover` highlight without scrolling. Cleared on
   * mouseleave/blur so the editor returns to the baseline anchored halo.
   */
  hoveredAnchoredElementId: string | null
  /**
   * Element ids of all blocks that are the target of at least one personal
   * note in the current entity. Maintained by the personal-note plugin's
   * `AnchoredNotesController` and read by the editor's `useAnchoredState` to
   * render the subtle baseline halo on every anchored block. Stored as an
   * array for Redux Toolkit / Immer compatibility — readers convert to a Set
   * if they need O(1) lookup.
   */
  anchoredElementIds: string[]
}

// Define the initial state using that type
const initialState: UIState = {
  sidebar: {
    open: false,
    activeTool: null,
    activeAgentId: null,
    pendingFocus: null,
    pendingDraft: null,
    pendingAutoSend: false,
  },
  pendingAnchoredNoteId: null,
  activeAnchoredElementId: null,
  hoveredAnchoredElementId: null,
  anchoredElementIds: [],
}

/**
 * @note if you get an Immer error here, pin the Immer version to ensure a single instance
 * @see https://github.com/reduxjs/redux-toolkit/issues/1181
 */
export const uiSlice = createSlice({
  name: 'ui',
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    setSidebarActiveTool: (state, action: PayloadAction<SidebarToolName | null>) => {
      state.sidebar.activeTool = action.payload
      state.sidebar.open = action.payload === null ? false : true
      if (action.payload !== 'Agents') {
        state.sidebar.activeAgentId = null
      }
    },
    toggleSidebarActiveTool: (state, action: PayloadAction<SidebarToolName>) => {
      state.sidebar.activeTool = state.sidebar.activeTool === action.payload ? null : action.payload
      state.sidebar.open = state.sidebar.activeTool === null ? false : true
      if (state.sidebar.activeTool !== 'Agents') {
        state.sidebar.activeAgentId = null
      }
    },

    toggleSidebar: state => {
      if (state.sidebar.activeTool === null) {
        state.sidebar.activeTool = 'Settings'
        state.sidebar.open = true
      } else {
        state.sidebar.activeTool = null
        state.sidebar.open = false
        state.sidebar.activeAgentId = null
      }
    },
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebar.open = action.payload
    },
    setSidebarActiveAgentId: (state, action: PayloadAction<string | null>) => {
      state.sidebar.activeAgentId = action.payload
    },
    setSidebarPendingFocus: (state, action: PayloadAction<AgentFocus | null>) => {
      state.sidebar.pendingFocus = action.payload
    },
    setSidebarPendingDraft: (state, action: PayloadAction<string | null>) => {
      state.sidebar.pendingDraft = action.payload
    },
    setSidebarPendingAutoSend: (state, action: PayloadAction<boolean>) => {
      state.sidebar.pendingAutoSend = action.payload
    },
    setPendingAnchoredNoteId: (state, action: PayloadAction<string | null>) => {
      state.pendingAnchoredNoteId = action.payload
    },
    setActiveAnchoredElementId: (state, action: PayloadAction<string | null>) => {
      state.activeAnchoredElementId = action.payload
    },
    setHoveredAnchoredElementId: (state, action: PayloadAction<string | null>) => {
      state.hoveredAnchoredElementId = action.payload
    },
    setAnchoredElementIds: (state, action: PayloadAction<string[]>) => {
      state.anchoredElementIds = action.payload
    },
  },
})
